import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronRight, Radar } from "lucide-react";
import { DetailFields, DetailSection, EditButton, RecordActions } from "../components/RecordView";
import { theme, card, emptyFrame, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { StatusTag, type StatusTone } from "../components/Drawing";
import { fmtDate, fmtDateTime } from "../lib/format";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { safeHref } from "../utils/safeHref";
import { AdvisoryAffectedAssets, type AffectedAssetCount } from "../components/AdvisoryAffectedAssets";
import { AdvisoryExposureStrip } from "../components/AdvisoryExposureStrip";
import { PortalLink } from "../components/PortalLink";
import { KIND_HREFS } from "../components/EntityTags";
import { RelatedRecords, riskLabel, type RelatedLink } from "../components/RelatedRecords";
import { NextSteps, type NextStepItem } from "../components/NextSteps";
import { RegisterToolbar, SummaryTiles } from "../components/RegisterToolbar";
import { ThreatBulkTriage } from "../components/ThreatBulkTriage";
import { downloadCsv, type SortOption } from "../lib/registerExport";
import { secondaryBtn, textarea as textareaStyle, input as inputStyle } from "./resilience/shared";

// ── Types ─────────────────────────────────────────────────────────────────

type Source   = "CISA_KEV" | "EPSS" | "OTX" | "NCSC" | "JCSC";
type Severity = "critical" | "high" | "medium" | "low" | "info";

interface ThreatItem {
  id:          string;
  source:      Source;
  externalId:  string;
  title:       string;
  summary:     string | null;
  severity:    Severity | null;
  publishedAt: string;
  url:         string | null;
  tags:        string[];
  cveIds?:     string[];
}

// Per-organisation triage — "threat information received and acted upon"
// (operational resilience module; NCSC CAF C1.f / A2.b). Keyed by item id.
type TriageStatus = "acknowledged" | "assessing" | "actioned" | "not_applicable";
interface Triage { status: TriageStatus; note: string | null; assessedBy: string | null; updatedAt: string; linkedRiskId: string | null; autoRaised?: boolean; untriaged?: boolean }

/** Backend lib/advisoryPriority.ts: the queue rank and why, in words. */
type Band = "P1" | "P2" | "P3" | "P4";
interface Priority { score: number; band: Band; tier: "confirmed" | "likely" | "possible" | "none"; reason: string; untriaged: boolean }
/** KEV listing and the highest EPSS / CVSS across the advisory's CVEs. */
interface Signals { kev: boolean; epss: number | null; cvss: number | null; kevCves: string[] }
type Affected = AffectedAssetCount & { topAssetId?: string | null };

interface ApiResponse {
  view?:      "feed" | "queue";
  items:      ThreatItem[];
  total:      number;
  lastSynced: string | null;
  triage:     Record<string, Triage>;
  /** Per item: how many of the organisation's active assets it affects (absent without an organisation in scope). */
  affectedAssets?: Record<string, Affected>;
  signals?:   Record<string, Signals>;
  priority?:  Record<string, Priority>;
  windowDays?: number;
}

interface Summary {
  affecting: number;
  untriagedAffecting: number;
  untriagedConfirmed: number;
  knownExploitedAffecting: number;
  knownExploitedUntriaged: number;
  triagedThisMonth: number;
  notApplicable: number;
  windowDays: number;
}

interface FeedSource {
  source: Source; name: string; countries: string[]; homepage: string; feed: "rss" | "none"; note: string | null; items: number; own: boolean;
}

// Theme tokens, never literals: one hex opts the badge out of the dark, light and Jersey schemes at once.
const TRIAGE_META: Record<TriageStatus, { label: string; tone: StatusTone; hint: string }> = {
  acknowledged:   { label: "Acknowledged",   tone: "none",    hint: "Seen; not yet assessed" },
  assessing:      { label: "Assessing",      tone: "partial", hint: "Working out whether it reaches us" },
  actioned:       { label: "Actioned",       tone: "met",     hint: "Patched, blocked or otherwise dealt with" },
  not_applicable: { label: "Not applicable", tone: "none",    hint: "Does not reach anything we run" },
};

// ── Display metadata ───────────────────────────────────────────────────────

const SOURCE_META: Record<Source, { label: string; color: string; bg: string }> = {
  CISA_KEV: { label: "CISA KEV",  color: theme.color.dangerText,  bg: theme.color.dangerBg },
  EPSS:     { label: "EPSS",      color: theme.color.accentText,  bg: theme.color.accentBg },
  OTX:      { label: "OTX",       color: theme.color.consultant,  bg: theme.color.consultantBg },
  NCSC:     { label: "NCSC",      color: theme.color.successText, bg: theme.color.successBg },
  JCSC:     { label: "JCSC",      color: theme.color.auditor,     bg: theme.color.auditorBg },
};

const SEVERITY_META: Record<Severity, { label: string; color: string; bg: string }> = {
  critical: { label: "Critical", color: theme.color.riskCritText, bg: theme.color.riskCritBg },
  high:     { label: "High",     color: theme.color.riskHighText, bg: theme.color.riskHighBg },
  medium:   { label: "Medium",   color: theme.color.riskMedText,  bg: theme.color.riskMedBg },
  low:      { label: "Low",      color: theme.color.riskLowText,  bg: theme.color.riskLowBg },
  info:     { label: "Info",     color: theme.color.muted,        bg: theme.color.panelAlt },
};

const BAND_META: Record<Band, { color: string; bg: string; action: "critical" | "high" | "medium" | "low" }> = {
  P1: { color: theme.color.riskCritText, bg: theme.color.riskCritBg, action: "critical" },
  P2: { color: theme.color.riskHighText, bg: theme.color.riskHighBg, action: "high" },
  P3: { color: theme.color.riskMedText,  bg: theme.color.riskMedBg,  action: "medium" },
  P4: { color: theme.color.riskLowText,  bg: theme.color.riskLowBg,  action: "low" },
};

interface RiskOption { id: string; name: string }

/**
 * What the organisation's record of this advisory still lacks. Asked only where
 * the advisory reaches something on the register; an advisory that touches
 * nothing needs no more than a status.
 */
function triageSteps(triage: Triage | undefined, affected: AffectedAssetCount | undefined): NextStepItem[] {
  if (!affected || affected.count === 0) return [];
  const confirmed = (affected.confirmed ?? 0) > 0;
  const closed = triage?.status === "actioned" || triage?.status === "not_applicable";
  return [
    { done: !!triage, label: "Triage it", hint: `It reaches ${affected.count} asset${affected.count === 1 ? "" : "s"} on your register${confirmed ? `, ${affected.confirmed} confirmed` : ""}.`, urgent: confirmed && !triage },
    { done: !!triage?.note, label: "Record what was decided or done" },
    { done: !!triage?.linkedRiskId || !(confirmed || affected.important > 0), label: "Link the register risk it raises", hint: "Expected where exposure is confirmed or an important asset is affected." },
    { done: closed, label: "Close it: actioned or not applicable" },
  ];
}

// ── Badges ─────────────────────────────────────────────────────────────────

const badge = (color: string, bg: string): React.CSSProperties => ({
  fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4,
  background: bg, color, whiteSpace: "nowrap",
});

function SourceBadge({ source }: { source: Source }) {
  const m = SOURCE_META[source];
  return <span style={badge(m.color, m.bg)}>{m.label}</span>;
}

function SeverityBadge({ severity }: { severity: Severity | null }) {
  if (!severity) return null;
  const m = SEVERITY_META[severity];
  return <span style={badge(m.color, m.bg)}>{m.label}</span>;
}

/** Known exploited (CISA KEV), the highest EPSS and the CVE ids the advisory carries. */
function SignalBadges({ item, signals }: { item: ThreatItem; signals: Signals | undefined }) {
  const cves = item.cveIds ?? [];
  if (!signals?.kev && signals?.epss == null && cves.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
      {signals?.kev && (
        <span title={signals.kevCves.length ? `In the CISA Known Exploited Vulnerabilities catalogue: ${signals.kevCves.join(", ")}` : "A CISA Known Exploited Vulnerabilities catalogue entry"} style={badge(theme.color.dangerText, theme.color.dangerBg)}>Known exploited</span>
      )}
      {signals?.epss != null && (
        <span title="FIRST EPSS: the probability this is exploited in the next 30 days (highest across its CVEs)" style={badge(signals.epss >= 0.1 ? theme.color.warningText : theme.color.muted, signals.epss >= 0.1 ? theme.color.warningBg : theme.color.panelAlt)}>EPSS {signals.epss.toFixed(2)}</span>
      )}
      {cves.slice(0, 6).map((c) => (
        <PortalLink key={c} to={`/vulnerabilities?cve=${encodeURIComponent(c)}`} title="Open in Vulnerabilities" style={{ fontSize: 10, padding: "1px 6px", borderRadius: 3, fontVariantNumeric: text.ref.fontVariantNumeric, background: theme.color.panelAlt, color: theme.color.text, border: `1px solid ${theme.color.border}`, textDecoration: "none" }}>{c}</PortalLink>
      ))}
      {cves.length > 6 && <span style={{ ...text.meta, color: theme.color.subtle }}>+{cves.length - 6} more</span>}
    </div>
  );
}

// ── Filter chip ────────────────────────────────────────────────────────────

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontSize: 12, fontWeight: 500, padding: "5px 12px", borderRadius: 3,
        cursor: "pointer", border: "1px solid",
        borderColor: active ? theme.color.accent : theme.color.border,
        background:  active ? theme.color.accentBg : "transparent",
        color:       active ? theme.color.accentText : theme.color.muted,
        transition:  "all 0.15s",
      }}
    >
      {label}
    </button>
  );
}

// ── Item card ─────────────────────────────────────────────────────────────
//
// A card in the list is a summary: source, severity, priority, title, date, the triage pill
// and how many assets it reaches. Opening it (a click on the title, or ?advisory=<id>) shows
// the read-only view of the advisory and the organisation's record of it; the triage form sits
// behind "Edit triage" and raising a corrective action under Actions (components/RecordView.tsx).

interface CardProps {
  item: ThreatItem;
  triage: Triage | undefined;
  affected: Affected | undefined;
  signals: Signals | undefined;
  priority: Priority | undefined;
  canTriage: boolean;
  canRaiseAction: boolean;
  risks: RiskOption[];
  selected: boolean;
  highlighted?: boolean;
  /** Open on first render (the deep-linked advisory). */
  defaultOpen?: boolean;
  onSelect: ((id: string, on: boolean) => void) | null;
  onTriage: (item: ThreatItem, status: TriageStatus, note: string, linkedRiskId: string | null) => Promise<void>;
}

function ItemCard({ item, triage, affected, signals, priority, canTriage, canRaiseAction, risks, selected, highlighted, defaultOpen, onSelect, onTriage }: CardProps) {
  const date = fmtDate(item.publishedAt);
  const [open, setOpen] = useState(!!defaultOpen);
  // The organisation's record of the advisory, edited behind Edit triage: status, what was done, the risk it raises.
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState<TriageStatus>(triage?.status ?? "acknowledged");
  const [note, setNote] = useState(triage?.note ?? "");
  const [riskId, setRiskId] = useState(triage?.linkedRiskId ?? "");
  const [assetLinks, setAssetLinks] = useState<RelatedLink[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<{ id: string; displayId: number | null } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // The affected assets become Linked rows; read once, when the view first opens.
  useEffect(() => {
    if (!open || assetLinks !== null || !affected || affected.count === 0) return;
    api.get<{ assets: Array<{ id: string; name: string; criticality: string; confidence: string }> }>(`/threat-feeds/${item.id}/assets`)
      .then((d) => setAssetLinks(d.assets.map((a) => ({ kindLabel: `Asset (${a.confidence})`, label: `${a.name} (${a.criticality})`, href: KIND_HREFS.asset(a.id) }))))
      .catch(() => setAssetLinks([]));
  }, [open, assetLinks, affected, item.id]);

  function startEditing() {
    setStatus(triage?.status ?? "acknowledged");
    setNote(triage?.note ?? "");
    setRiskId(triage?.linkedRiskId ?? "");
    setError(null);
    setEditing(true);
  }

  async function save() {
    if ((status === "actioned" || status === "not_applicable") && !note.trim()) {
      setError(status === "actioned" ? "Say what was done." : "Say why it does not apply."); return;
    }
    setBusy(true); setError(null);
    // Back to the view, showing the updated record.
    try { await onTriage(item, status, note, riskId || null); setEditing(false); }
    catch (e) { setError(apiErrorMessage(e, "Could not save the triage")); }
    finally { setBusy(false); }
  }

  // A corrective action to fix what the advisory exposes, against the most certain, most important asset it reaches.
  async function raiseAction() {
    setActionBusy(true); setActionError(null);
    try {
      const cves = (item.cveIds ?? []).slice(0, 10).join(", ");
      const res = await api.post<{ action: { id: string; displayId: number | null } }>("/improvement/actions", {
        title: `Remediate advisory: ${item.title}`.slice(0, 300),
        description: [
          `${SOURCE_META[item.source].label} ${item.externalId}${item.severity ? ` (${item.severity})` : ""}.`,
          cves ? `CVEs: ${cves}.` : "",
          priority ? `Triage priority ${priority.band}: ${priority.reason}` : "",
          item.url ? `Advisory: ${item.url}` : "",
        ].filter(Boolean).join("\n"),
        source: "attention",
        sourceRef: `advisory:${item.id}`,
        priority: priority ? BAND_META[priority.band].action : "medium",
        assetId: affected?.topAssetId ?? null,
      });
      setAction(res.action);
    } catch (e) {
      setActionError(apiErrorMessage(e, "Could not raise the corrective action"));
    } finally {
      setActionBusy(false);
    }
  }

  const steps = triageSteps(triage, affected);
  const linkedRisk = triage?.linkedRiskId ? risks.find((r) => r.id === triage.linkedRiskId) : null;
  const shownRiskId = editing ? riskId : triage?.linkedRiskId ?? "";
  const links: RelatedLink[] = [
    ...(shownRiskId ? [{ kindLabel: "Register risk", label: risks.find((r) => r.id === shownRiskId)?.name ?? "Linked risk", href: KIND_HREFS.risk(shownRiskId) }] : []),
    ...(assetLinks ?? []),
  ];
  const humanTriage = triage && !triage.untriaged ? triage : triage?.autoRaised ? triage : undefined;
  const reaches = affected && affected.count > 0;
  const showRaise = canRaiseAction && reaches && !action;

  const triagePill = humanTriage
    ? <StatusTag tone={TRIAGE_META[humanTriage.status].tone}>{TRIAGE_META[humanTriage.status].label}</StatusTag>
    : <span style={{ ...text.meta, color: affected && (affected.confirmed ?? 0) > 0 ? theme.color.dangerText : theme.color.faint, fontWeight: reaches ? 600 : 400 }}>
        {reaches ? "Not yet triaged, and it reaches your assets" : "Not yet triaged for your organisation"}
      </span>;

  return (
    <div id={`advisory-${item.id}`} style={{
      ...card,
      padding: "14px 18px",
      display: "flex",
      flexDirection: "column",
      gap: 6,
      ...(highlighted ? { border: `2px solid ${theme.color.accent}` } : {}),
      ...(selected ? { background: theme.color.accentBg } : {}),
    }}>
      {/* Summary: what the list shows */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {onSelect && (
          <input type="checkbox" checked={selected} onChange={(e) => onSelect(item.id, e.target.checked)} aria-label={`Select ${item.title}`} style={{ margin: "0 4px 0 0", accentColor: theme.color.accent }} />
        )}
        {priority && priority.tier !== "none" && (
          <span title={`Triage priority, score ${priority.score} of 100`} style={badge(BAND_META[priority.band].color, BAND_META[priority.band].bg)}>{priority.band}</span>
        )}
        <SourceBadge source={item.source} />
        <SeverityBadge severity={item.severity} />
        {signals?.kev && <span style={badge(theme.color.dangerText, theme.color.dangerBg)}>Known exploited</span>}
        <span style={{ ...text.meta, color: theme.color.muted, marginLeft: "auto" }}>{date}</span>
      </div>

      <button type="button" onClick={() => { setOpen((o) => !o); setEditing(false); }} aria-expanded={open}
        style={{ display: "flex", alignItems: "flex-start", gap: 6, padding: 0, border: "none", background: "transparent", cursor: "pointer", textAlign: "left", color: theme.color.text }}>
        {open ? <ChevronDown size={15} style={{ flexShrink: 0, marginTop: 2 }} /> : <ChevronRight size={15} style={{ flexShrink: 0, marginTop: 2 }} />}
        <span style={{ ...text.itemTitle, margin: 0 }}>{item.title}</span>
      </button>

      {!open && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {triagePill}
          {humanTriage && <span style={{ ...text.meta, color: theme.color.muted }}>{fmtDate(humanTriage.updatedAt)}</span>}
          {reaches && (
            <span style={{ ...text.meta, color: theme.color.muted }}>
              · Reaches {affected!.count} asset{affected!.count === 1 ? "" : "s"}{(affected!.confirmed ?? 0) > 0 ? `, ${affected!.confirmed} confirmed` : ""}
            </span>
          )}
          {(item.cveIds?.length ?? 0) > 0 && <span style={{ ...text.meta, color: theme.color.subtle }}>· {item.cveIds!.length} CVE{item.cveIds!.length === 1 ? "" : "s"}</span>}
        </div>
      )}

      {/* The read-only view */}
      {open && (
        <div style={{ display: "grid", gap: 0, marginTop: 4 }}>
          <DetailSection title="Advisory" aside={item.url ? (
            <a href={safeHref(item.url)} target="_blank" rel="noopener noreferrer" style={{ ...text.hint, color: theme.color.accent, textDecoration: "none", fontWeight: 500 }}>Open the source →</a>
          ) : undefined}>
            <DetailFields fields={[
              { label: "Source", value: `${SOURCE_META[item.source].label} ${item.externalId}` },
              { label: "Published", value: date },
              { label: "Severity", value: item.severity ? SEVERITY_META[item.severity].label : null },
              { label: "Priority", value: priority && priority.tier !== "none" ? `${priority.band} (score ${priority.score} of 100)` : null },
              ...(priority && priority.tier !== "none" ? [{ label: "Why it is here", value: priority.reason, wide: true }] : []),
              { label: "Summary", value: item.summary, wide: true, multiline: true },
              { label: "Tags", value: item.tags.length ? item.tags.join(", ") : null, wide: true },
            ]} />
          </DetailSection>

          <DetailSection title="Vulnerabilities">
            <div style={{ display: "grid", gap: 8 }}>
              <DetailFields fields={[
                { label: "Known exploited", value: signals ? (signals.kev ? (signals.kevCves.length ? `Yes: ${signals.kevCves.join(", ")}` : "Yes") : "No") : null },
                { label: "Highest EPSS", value: signals?.epss != null ? signals.epss.toFixed(2) : null },
                { label: "Highest CVSS", value: signals?.cvss != null ? String(signals.cvss) : null },
              ]} />
              <SignalBadges item={item} signals={signals} />
            </div>
          </DetailSection>

          <DetailSection title="Affected assets">
            {reaches
              ? <AdvisoryAffectedAssets itemId={item.id} affected={affected} />
              : <div style={{ ...text.hint }}>It reaches nothing on your asset register.</div>}
          </DetailSection>

          <DetailSection title="Your triage" aside={canTriage && !editing ? <EditButton onClick={startEditing} label={triage ? "Edit triage" : "Triage"} /> : undefined}>
            {!editing ? (
              <div style={{ display: "grid", gap: 10 }}>
                <DetailFields fields={[
                  { label: "Where it stands", value: triagePill },
                  { label: "Recorded by", value: humanTriage ? (humanTriage.assessedBy ?? (humanTriage.autoRaised ? "Automatic, not yet reviewed" : null)) : null },
                  { label: "Last updated", value: humanTriage ? fmtDate(humanTriage.updatedAt) : null },
                  { label: "Register risk it raises", value: humanTriage?.linkedRiskId ? (
                    <PortalLink to={KIND_HREFS.risk(humanTriage.linkedRiskId) ?? "/risk-register"} style={{ color: theme.color.accentText, textDecoration: "none", fontWeight: 600 }}>{linkedRisk ? linkedRisk.name : "Register entry"}</PortalLink>
                  ) : null },
                  { label: "What was decided or done", value: humanTriage?.note, wide: true, multiline: true },
                ]} />
                {steps.length > 0 && <NextSteps items={steps} title="Your record of this advisory" />}
              </div>
            ) : (
              <div style={{ display: "grid", gap: 10, padding: 12, borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panelAlt }}>
                <div>
                  <div style={{ ...text.label, marginBottom: 6 }}>Where it stands</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {(Object.keys(TRIAGE_META) as TriageStatus[]).map((k) => (
                      <button key={k} type="button" title={TRIAGE_META[k].hint} onClick={() => setStatus(k)} style={{
                        fontSize: text.hint.fontSize, padding: "4px 10px", borderRadius: 3, cursor: "pointer",
                        border: status === k ? `1px solid ${theme.color.accent}` : `1px solid ${theme.color.border}`,
                        background: status === k ? theme.color.accentBg : "transparent",
                        color: status === k ? theme.color.accentText : theme.color.muted, fontWeight: status === k ? 600 : 400,
                      }}>{TRIAGE_META[k].label}</button>
                    ))}
                  </div>
                  <div style={{ ...text.meta, marginTop: 4 }}>{TRIAGE_META[status].hint}.</div>
                </div>
                <label style={{ display: "grid", gap: 4 }}>
                  <span style={{ ...text.label, marginBottom: 0 }}>{status === "not_applicable" ? "Why it does not apply" : "What was decided or done"}{status === "actioned" || status === "not_applicable" ? " *" : ""}</span>
                  <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} style={textareaStyle} placeholder={status === "actioned" ? "Patched, blocked, assessed not exposed…" : status === "not_applicable" ? "We do not run the product; the version we run is not affected…" : "Who is looking at it and what happens next"} />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span style={{ ...text.label, marginBottom: 0 }}>Register risk it raises</span>
                  <select value={riskId} onChange={(e) => setRiskId(e.target.value)} style={inputStyle}>
                    <option value="">None</option>
                    {riskId && !risks.some((r) => r.id === riskId) && <option value={riskId}>Current register entry</option>}
                    {risks.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </label>
                {error && <div style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button type="button" onClick={() => setEditing(false)} style={secondaryBtn}>Cancel</button>
                  <button type="button" disabled={busy} onClick={save} style={{ ...btnPrimary, padding: "6px 14px", fontSize: text.hint.fontSize, opacity: busy ? 0.6 : 1 }}>{busy ? "Saving…" : "Save triage"}</button>
                </div>
              </div>
            )}
          </DetailSection>

          {links.length > 0 && (
            <DetailSection title="Linked records">
              <RelatedRecords entity={null} links={links} />
            </DetailSection>
          )}

          {(showRaise || action) && (
            <RecordActions>
              {showRaise && (
                <button type="button" disabled={actionBusy} onClick={raiseAction} title="Raise a corrective action against the most affected asset" style={{ ...secondaryBtn, padding: "4px 10px", fontSize: text.meta.fontSize, opacity: actionBusy ? 0.6 : 1 }}>
                  {actionBusy ? "Raising…" : "Raise corrective action"}
                </button>
              )}
              {action && (
                <PortalLink to={KIND_HREFS.action(action.id) ?? "/improvement"} style={{ ...text.meta, color: theme.color.successText, textDecoration: "none", fontWeight: 600 }}>
                  {action.displayId ? `CA-${action.displayId} raised` : "Corrective action raised"}
                </PortalLink>
              )}
              {actionError && <div style={{ ...text.hint, color: theme.color.dangerText, flexBasis: "100%" }}>{actionError}</div>}
            </RecordActions>
          )}
        </div>
      )}
    </div>
  );
}


// ── Page ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

type View = "queue" | "feed";
type SortKey = "priority" | "newest" | "severity";
type TriageFilter = "" | "untriaged" | "not_applicable" | "month";

// The rows are sorted by the server (the queue's rank is the organisation's own); the toolbar only picks the order.
const noCompare = () => 0;
const QUEUE_SORTS: SortOption<ThreatItem>[] = [
  { key: "priority", label: "Priority", compare: noCompare },
  { key: "newest", label: "Newest", compare: noCompare },
  { key: "severity", label: "Severity", compare: noCompare },
];
const FEED_SORTS: SortOption<ThreatItem>[] = QUEUE_SORTS.filter((s) => s.key !== "priority");

const ymd = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : "");

export function ThreatAdvisoriesPage() {
  const { user } = useCurrentUser();
  // Triage is a risk decision: mirrors the risks:write requirement on PUT /threat-feeds/:id/triage.
  const canTriage = !!user?.permissions?.includes("risks:write");
  const canRecompute = !!user?.permissions?.includes("compliance:run");
  // POST /improvement/actions needs controls:write.
  const canRaiseAction = !!user?.permissions?.includes("controls:write");
  // POST /threat-feeds/sync re-pulls every feed for the whole platform: consultants only
  // (the route refuses clients) and, like every write in that router, compliance:run.
  const canSyncFeeds = user?.role === "consultant" && canRecompute;
  const [feedSync, setFeedSync] = useState<{ busy: boolean; message: string | null; error: boolean }>({ busy: false, message: null, error: false });
  const [searchParams, setSearchParams] = useSearchParams();
  const [sources,   setSources]   = useState<FeedSource[]>([]);
  const [data,      setData]      = useState<ApiResponse | null>(null);
  const [summary,   setSummary]   = useState<Summary | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [source,    setSource]    = useState<Source | "">("");
  const [severity,  setSeverity]  = useState<Severity | "">("");
  const [search,    setSearch]    = useState("");
  const [searchVal, setSearchVal] = useState("");
  const [offset,    setOffset]    = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [risks, setRisks] = useState<RiskOption[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [deepLinked, setDeepLinked] = useState<ApiResponse | null>(null);

  const setParam = useCallback((changes: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(changes)) { if (v) next.set(k, v); else next.delete(k); }
    setSearchParams(next, { replace: true });
    setOffset(0);
    setSelected([]);
  }, [searchParams, setSearchParams]);

  // ?affecting=1 (from My Actions, the emails and the Teams card) or ?affecting=confirmed: only advisories with an open exposure.
  const affectingParam = searchParams.get("affecting");
  const affecting: "" | "any" | "confirmed" = affectingParam === "confirmed" ? "confirmed" : affectingParam ? "any" : "";
  const setAffecting = (v: "" | "any" | "confirmed") => setParam({ affecting: v ? (v === "any" ? "1" : v) : null });

  // The triage queue is the default while anything reaching the organisation is untriaged.
  const viewParam = searchParams.get("view");
  const view: View = viewParam === "queue" || viewParam === "feed" ? viewParam : summary && summary.untriagedAffecting > 0 ? "queue" : "feed";
  const sortParam = searchParams.get("sort") as SortKey | null;
  const sort: SortKey = view === "queue" ? (sortParam ?? "priority") : sortParam === "severity" ? "severity" : "newest";
  const triageParam = searchParams.get("triage");
  const triageFilter: TriageFilter = triageParam === "untriaged" || triageParam === "not_applicable" || triageParam === "month" ? triageParam : "";
  const kevOnly = searchParams.get("kev") === "1";
  const advisoryId = searchParams.get("advisory");

  useEffect(() => {
    api.get<{ sources: FeedSource[] }>("/threat-feeds/sources").then((d) => setSources(d.sources)).catch(() => setSources([]));
  }, []);
  // The register risks a triage can link to, read once.
  useEffect(() => {
    api.get<{ risks: Array<{ id: string; title: string; displayId: number | null }> }>("/risks")
      .then((d) => setRisks(d.risks.map((r) => ({ id: r.id, name: riskLabel(r) }))))
      .catch(() => setRisks([]));
  }, []);
  // The tiles: re-read after every triage change.
  const [summaryDone, setSummaryDone] = useState(false);
  useEffect(() => {
    api.get<Summary>("/threat-feeds/summary").then(setSummary).catch(() => setSummary(null)).finally(() => setSummaryDone(true));
  }, [reloadKey]);

  // Search is the server's (titles and summaries); typing settles before it asks.
  useEffect(() => {
    const t = window.setTimeout(() => { setSearch(searchVal.trim()); setOffset(0); }, 350);
    return () => window.clearTimeout(t);
  }, [searchVal]);

  // Wait for the tiles before the first read, so the default view is decided once.
  const viewReady = !!viewParam || summaryDone;
  useEffect(() => {
    if (!viewReady) return;
    setLoading(true);
    const params: Record<string, string> = { limit: String(PAGE_SIZE), offset: String(offset), view, sort };
    if (source)   params.source = source;
    if (severity) params.severity = severity;
    if (search)   params.search = search;
    if (affecting) params.affecting = affecting;
    if (triageFilter) params.triage = triageFilter;
    if (kevOnly) params.kev = "1";
    api.get<ApiResponse>("/threat-feeds", params)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [viewReady, view, sort, source, severity, search, offset, affecting, triageFilter, kevOnly, reloadKey]);

  // ?advisory=<id>: the linked advisory, pinned above the list and scrolled to.
  const scrolledTo = useRef<string | null>(null);
  useEffect(() => {
    if (!advisoryId) { setDeepLinked(null); return; }
    api.get<ApiResponse>("/threat-feeds", { id: advisoryId, limit: "1" })
      .then((d) => setDeepLinked(d))
      .catch(() => setDeepLinked({ items: [], total: 0, lastSynced: null, triage: {} }));
  }, [advisoryId, reloadKey]);
  useEffect(() => {
    if (!deepLinked?.items.length || scrolledTo.current === advisoryId) return;
    scrolledTo.current = advisoryId;
    window.setTimeout(() => document.getElementById(`advisory-${deepLinked.items[0].id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }, [deepLinked, advisoryId]);

  // linkedRiskId is always sent: the route replaces it, so leaving it out would unlink the register entry.
  const triageItem = useCallback(async (item: ThreatItem, status: TriageStatus, note: string, linkedRiskId: string | null) => {
    const res = await api.put<{ triage: Triage }>(`/threat-feeds/${item.id}/triage`, { status, note: note || null, linkedRiskId });
    const t = { ...res.triage, untriaged: false };
    setData((d) => (d ? { ...d, triage: { ...d.triage, [item.id]: t } } : d));
    setDeepLinked((d) => (d ? { ...d, triage: { ...d.triage, [item.id]: t } } : d));
    api.get<Summary>("/threat-feeds/summary").then(setSummary).catch(() => undefined);
  }, []);

  const ownSources = sources.filter((s) => s.own);
  const items = useMemo(() => data?.items ?? [], [data]);
  const setSourceFilter = (s: Source | "") => { setSource(s); setOffset(0); setSelected([]); };
  const setSevFilter    = (s: Severity | "") => { setSeverity(s); setOffset(0); setSelected([]); };

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const filtered = !!(source || severity || search || affecting || triageFilter || kevOnly);

  function exportCsv() {
    const d = data;
    if (!d) return;
    downloadCsv<ThreatItem>("threat-advisories", [
      { header: "Published", value: (i) => ymd(i.publishedAt) },
      { header: "Source", value: (i) => SOURCE_META[i.source]?.label ?? i.source },
      { header: "Reference", value: (i) => i.externalId },
      { header: "Title", value: (i) => i.title },
      { header: "Severity", value: (i) => i.severity ?? "" },
      { header: "Priority", value: (i) => d.priority?.[i.id]?.band ?? "" },
      { header: "Priority score", value: (i) => d.priority?.[i.id]?.score ?? "" },
      { header: "Why", value: (i) => d.priority?.[i.id]?.reason ?? "" },
      { header: "Known exploited", value: (i) => !!d.signals?.[i.id]?.kev },
      { header: "EPSS", value: (i) => d.signals?.[i.id]?.epss?.toFixed(3) ?? "" },
      { header: "CVSS", value: (i) => d.signals?.[i.id]?.cvss ?? "" },
      { header: "CVEs", value: (i) => (i.cveIds ?? []).join(" ") },
      { header: "Assets reached", value: (i) => d.affectedAssets?.[i.id]?.count ?? 0 },
      { header: "Confirmed", value: (i) => d.affectedAssets?.[i.id]?.confirmed ?? 0 },
      { header: "Likely", value: (i) => d.affectedAssets?.[i.id]?.likely ?? 0 },
      { header: "Possible", value: (i) => d.affectedAssets?.[i.id]?.possible ?? 0 },
      { header: "Triage", value: (i) => { const t = d.triage?.[i.id]; return !t || t.untriaged ? "Untriaged" : TRIAGE_META[t.status]?.label ?? t.status; } },
      { header: "Triage note", value: (i) => d.triage?.[i.id]?.note ?? "" },
      { header: "Triaged by", value: (i) => d.triage?.[i.id]?.assessedBy ?? "" },
      { header: "Triaged on", value: (i) => ymd(d.triage?.[i.id]?.updatedAt) },
      { header: "Link", value: (i) => i.url ?? "" },
    ], items);
  }

  const toggleSelect = (id: string, on: boolean) => setSelected((s) => (on ? [...new Set([...s, id])] : s.filter((x) => x !== id)));

  const tileActive = {
    untriaged: view === "queue" && triageFilter === "untriaged" && !kevOnly,
    kev: view === "queue" && kevOnly && !triageFilter,
    month: view === "feed" && triageFilter === "month",
    na: view === "feed" && triageFilter === "not_applicable",
  };
  const toggleTile = (active: boolean, changes: Record<string, string | null>) =>
    setParam(active ? { triage: null, kev: null } : { triage: null, kev: null, ...changes });

  return (
    <div className="page-pad">
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
            <Radar size={20} /> Threat intelligence
          </h1>
          {canSyncFeeds && (
            <button type="button" style={{ ...secondaryBtn, opacity: feedSync.busy ? 0.6 : 1 }} disabled={feedSync.busy} onClick={async () => {
              setFeedSync({ busy: true, message: null, error: false });
              try {
                await api.post("/threat-feeds/sync", {});
                setFeedSync({ busy: false, message: "Feed refresh queued. New advisories appear once it finishes.", error: false });
              } catch (e) {
                setFeedSync({ busy: false, message: apiErrorMessage(e, "Could not queue the feed refresh."), error: true });
              }
            }}>
              {feedSync.busy ? "Queuing…" : "Refresh feeds"}
            </button>
          )}
        </div>
        {feedSync.message && (
          <div style={{ ...text.hint, color: feedSync.error ? theme.color.dangerText : theme.color.successText, marginBottom: 6 }}>{feedSync.message}</div>
        )}
        <p style={{ ...pageSubtitle, maxWidth: 760 }}>
          Live advisories from CISA KEV, EPSS, AlienVault OTX and the national CERTs, synced daily. Work the triage queue so the record shows each advisory that reaches you was acted upon.
          {data?.lastSynced && (
            <> Last synced {fmtDateTime(data.lastSynced)}.</>
          )}
        </p>
      </div>

      {summary && (
        <SummaryTiles tiles={[
          { key: "untriaged", label: "Untriaged, affecting you", value: summary.untriagedAffecting, hint: summary.untriagedConfirmed ? `${summary.untriagedConfirmed} confirmed on an asset` : `Exposed, or named in the last ${summary.windowDays} days`, tone: summary.untriagedConfirmed ? "danger" : summary.untriagedAffecting ? "warning" : "success", active: tileActive.untriaged, onClick: () => toggleTile(tileActive.untriaged, { view: "queue", triage: "untriaged" }) },
          { key: "kev", label: "Known exploited, affecting you", value: summary.knownExploitedAffecting, hint: summary.knownExploitedUntriaged ? `${summary.knownExploitedUntriaged} untriaged` : "All triaged", tone: summary.knownExploitedUntriaged ? "danger" : "neutral", active: tileActive.kev, onClick: () => toggleTile(tileActive.kev, { view: "queue", kev: "1" }) },
          { key: "month", label: "Triaged this month", value: summary.triagedThisMonth, tone: "accent", active: tileActive.month, onClick: () => toggleTile(tileActive.month, { view: "feed", triage: "month" }) },
          { key: "na", label: "Not applicable", value: summary.notApplicable, hint: "Recorded as not reaching you", active: tileActive.na, onClick: () => toggleTile(tileActive.na, { view: "feed", triage: "not_applicable" }) },
        ]} />
      )}

      {/* What the evidence says: confirmed / likely exposures, the sources behind them, the automatic register entry switch */}
      <AdvisoryExposureStrip canToggleRisk={canTriage} canRecompute={canRecompute} affecting={affecting} onAffectingChange={setAffecting} onRecomputed={() => setReloadKey((k) => k + 1)} />

      {/* ?advisory=<id> */}
      {advisoryId && deepLinked && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ ...text.sectionTitle, margin: 0 }}>Linked advisory</span>
            <button type="button" onClick={() => setParam({ advisory: null })} style={{ ...secondaryBtn, padding: "3px 10px", fontSize: text.meta.fontSize }}>Close</button>
          </div>
          {deepLinked.items.length ? (
            <ItemCard item={deepLinked.items[0]} triage={deepLinked.triage?.[deepLinked.items[0].id]} affected={deepLinked.affectedAssets?.[deepLinked.items[0].id]} signals={deepLinked.signals?.[deepLinked.items[0].id]} priority={deepLinked.priority?.[deepLinked.items[0].id]} canTriage={canTriage} canRaiseAction={canRaiseAction} risks={risks} selected={false} highlighted defaultOpen onSelect={null} onTriage={triageItem} />
          ) : (
            <div style={{ ...emptyFrame, ...text.body, textAlign: "left", padding: 16, color: theme.color.muted }}>That advisory is no longer in the feed.</div>
          )}
        </div>
      )}

      {/* The organisation's own CERTs */}
      {ownSources.length > 0 && (
        <div style={{ ...card, padding: "12px 18px", marginBottom: 16, display: "grid", gap: 8 }}>
          <p style={{ ...text.sectionTitle, margin: 0 }}>Your national CERTs</p>
          {ownSources.map((s) => (
            <div key={s.source} style={{ ...text.hint, display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
              <SourceBadge source={s.source} />
              <div style={{ flex: 1, minWidth: 240, color: theme.color.muted, lineHeight: 1.5 }}>
                <a href={safeHref(s.homepage)} target="_blank" rel="noopener noreferrer" style={{ color: theme.color.accent, textDecoration: "none", fontWeight: 600 }}>{s.name}</a>
                {" "}· {s.items.toLocaleString()} item{s.items === 1 ? "" : "s"}
                {s.feed === "none" && s.note && <span style={{ display: "block", color: theme.color.subtle }}>{s.note}</span>}
              </div>
              {s.items > 0 && <Chip label="Show" active={source === s.source} onClick={() => setSourceFilter(source === s.source ? "" : s.source)} />}
            </div>
          ))}
        </div>
      )}

      {/* View */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <Chip label={`Triage queue${summary ? ` (${summary.affecting})` : ""}`} active={view === "queue"} onClick={() => setParam({ view: "queue", sort: null, triage: triageFilter === "month" || triageFilter === "not_applicable" ? null : triageFilter || null })} />
        <Chip label="All advisories" active={view === "feed"} onClick={() => setParam({ view: "feed", sort: null, kev: kevOnly ? "1" : null })} />
        <span style={{ ...text.hint, marginLeft: 6 }}>
          {view === "queue"
            ? `Advisories that reach your register: exposed on an asset, or naming one in the last ${summary?.windowDays ?? 30} days. Untriaged first, then by priority.`
            : "Every advisory in the feed, with your record of each."}
        </span>
      </div>

      {/* Filters */}
      <div style={{ ...card, padding: "14px 18px", marginBottom: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <p style={{ ...text.label, margin: "0 0 8px" }}>Source</p>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Chip label="All"      active={source === ""}          onClick={() => setSourceFilter("")} />
            <Chip label="CISA KEV" active={source === "CISA_KEV"}  onClick={() => setSourceFilter("CISA_KEV")} />
            <Chip label="EPSS"     active={source === "EPSS"}      onClick={() => setSourceFilter("EPSS")} />
            <Chip label="OTX"      active={source === "OTX"}       onClick={() => setSourceFilter("OTX")} />
            <Chip label="NCSC"     active={source === "NCSC"}      onClick={() => setSourceFilter("NCSC")} />
            <Chip label="JCSC"     active={source === "JCSC"}      onClick={() => setSourceFilter("JCSC")} />
          </div>
        </div>
        <div>
          <p style={{ ...text.label, margin: "0 0 8px" }}>Severity</p>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {(["", "critical", "high", "medium", "low", "info"] as const).map(s => (
              <Chip key={s} label={s === "" ? "All" : SEVERITY_META[s as Severity].label} active={severity === s} onClick={() => setSevFilter(s as Severity | "")} />
            ))}
          </div>
        </div>
      </div>

      <RegisterToolbar<ThreatItem>
        search={searchVal}
        onSearch={setSearchVal}
        placeholder="Search titles and summaries"
        sortOptions={view === "queue" ? QUEUE_SORTS : FEED_SORTS}
        sort={sort}
        onSort={(k) => setParam({ sort: k })}
        count={data ? { shown: items.length, total: data.total } : undefined}
        onExport={items.length ? exportCsv : undefined}
        exportLabel="Export page (CSV)"
      >
        {(filtered) && (
          <button type="button" onClick={() => { setSearchVal(""); setSearch(""); setSource(""); setSeverity(""); setParam({ affecting: null, triage: null, kev: null }); }} style={{ ...secondaryBtn, padding: "6px 10px", fontSize: text.hint.fontSize }}>
            Clear filters
          </button>
        )}
      </RegisterToolbar>

      {canTriage && (
        <ThreatBulkTriage<Triage>
          selected={selected}
          pageCount={items.length}
          onClear={() => setSelected([])}
          onSelectAll={() => setSelected(items.map((i) => i.id))}
          onDone={() => { setSelected([]); setReloadKey((k) => k + 1); }}
        />
      )}

      {/* Items */}
      {loading && !data ? (
        <div style={{ ...text.body, textAlign: "center", padding: 60, color: theme.color.muted }}>
          Loading threat intelligence…
        </div>
      ) : !data || data.items.length === 0 ? (
        <div style={{ ...emptyFrame, ...text.body, padding: 48, color: theme.color.muted }}>
          {view === "queue" && !filtered
            ? "Nothing in the queue: no advisory currently reaches an asset on your register. Exposures are re-evaluated after every feed sync, NVD read, Intune / Defender sync and discovery pass."
            : affecting
            ? "No advisory currently reaches an asset on your register. Exposures are re-evaluated after every feed sync, NVD read, Intune / Defender sync and discovery pass."
            : data?.total === 0 && filtered
            ? "No items match your filters. The feed syncs daily at 06:00 UTC."
            : "No threat intelligence data yet. The first sync runs at 06:00 UTC."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, opacity: loading ? 0.6 : 1 }}>
          {data.items.map(item => (
            <ItemCard
              key={item.id}
              item={item}
              triage={data.triage?.[item.id]}
              affected={data.affectedAssets?.[item.id]}
              signals={data.signals?.[item.id]}
              priority={data.priority?.[item.id]}
              canTriage={canTriage}
              canRaiseAction={canRaiseAction}
              risks={risks}
              selected={selected.includes(item.id)}
              highlighted={item.id === advisoryId}
              onSelect={canTriage ? toggleSelect : null}
              onTriage={triageItem}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 24 }}>
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => { setOffset(o => Math.max(0, o - PAGE_SIZE)); setSelected([]); }}
            style={{ ...text.body, padding: "7px 14px", borderRadius: theme.radius.sm, cursor: "pointer", border: `1px solid ${theme.color.border}`, background: "transparent", color: offset === 0 ? theme.color.faint : theme.color.text }}
          >
            ← Previous
          </button>
          <span style={{ ...text.body, color: theme.color.muted }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= (data?.total ?? 0)}
            onClick={() => { setOffset(o => o + PAGE_SIZE); setSelected([]); }}
            style={{ ...text.body, padding: "7px 14px", borderRadius: theme.radius.sm, cursor: "pointer", border: `1px solid ${theme.color.border}`, background: "transparent", color: offset + PAGE_SIZE >= (data?.total ?? 0) ? theme.color.faint : theme.color.text }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
